import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Circle, Sparkles } from "lucide-react";
import { commissionsApi, type ClaimStatus, type CommissionAuditLogDto } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { ClaimLoan } from "@/components/commissions/ClaimLoan";
import { ClaimClaimant, HeldPositionNote } from "@/components/commissions/ClaimHeld";
import { CorrectAttributionModal } from "@/components/commissions/CorrectAttributionModal";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, Card } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  ClaimStatusBadge,
  ESCALATION_ACTIONABLE_STATUSES,
  WITHDRAWABLE_STATUSES,
  RevisionStateBadge,
  claimStatusLabel,
} from "@/components/commissions/ClaimStatusBadge";
import {
  calendarDate,
  currency,
  datetime,
  percent,
  shortDate,
} from "@/components/commissions/formatters";
import { useAuth } from "@/hooks/useAuth";
import { ContestModal } from "./ContestModal";
import { AffirmModal } from "./AffirmModal";
import { ResolveEscalationModal } from "./ResolveEscalationModal";
import { ConfirmRevenueModal } from "./ConfirmRevenueModal";
import { RecordPaymentModal } from "./RecordPaymentModal";
import { AdjustmentModal } from "./AdjustmentModal";
import { DiscussionThread } from "./DiscussionThread";
import { formatContestedFields } from "./contestableFields";
import { useDateFormatter } from "@/utils/datetime";

const TIMELINE_ORDER: ClaimStatus[] = [
  "Draft",
  "Submitted",
  "UnderReview",
  "Agreed",
  "Payable",
  "PartiallyPaid",
  "PaidInFull",
];

/** The statuses in which a claim sits in the review queue — the statuses
 *  `GetQueuePositionAsync` counts. */
function isWaitingInReviewQueue(status: ClaimStatus | undefined): boolean {
  return status === "Submitted" || status === "Revised";
}

export function ClaimDetailPage() {
  const { tz } = useDateFormatter();
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showContest, setShowContest] = useState(false);
  const [showConfirmRevenue, setShowConfirmRevenue] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [showAffirm, setShowAffirm] = useState(false);
  const [showResolveEscalation, setShowResolveEscalation] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showCorrectAttribution, setShowCorrectAttribution] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");

  const detail = useQuery({
    queryKey: commissionKeys.claim(id),
    queryFn: () => commissionsApi.getClaim(id),
    enabled: !!id,
  });

  // "You are #n in the review queue" is the CLAIMANT's answer, so it is asked
  // only for the claimant's own claim while that claim is waiting in the queue.
  // It used to be asked for anyone who opened a Submitted claim, so an
  // administrator reviewing someone else's claim was told THEY were in the
  // queue. The banner below checks both conditions again: a query that stops
  // being enabled keeps its last answer, which is how the banner outlived
  // "Begin review".
  const ownClaim = !!user && detail.data?.summary.claimantUserId === user.id;
  const position = useQuery({
    queryKey: commissionKeys.claimPosition(id),
    queryFn: () => commissionsApi.queuePosition(id),
    enabled: ownClaim && isWaitingInReviewQueue(detail.data?.summary.status),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: commissionKeys.all });

  // 🔴 `pickUp` AND `agree` ARE INVOKED. They were not: both were declared as
  // mutations, both had `loading={x.isPending}` on their buttons, and neither
  // was ever `.mutate()`d — the buttons called a local `handle` helper that hit
  // the API directly, so `isPending` was permanently false. The comment below,
  // written for `withdraw`, says in terms why that is wrong, and the two
  // mutations it does not cover sat immediately above it.
  //
  // The options object is copied from `withdraw` WHOLESALE rather than just
  // pointing the buttons at `.mutate()`. `handle` supplied two things these two
  // lacked: a `.catch` that wrote the server's message into the error banner,
  // and a `setError(null)` on success that cleared a previous failure. Swapping
  // the call site alone would have traded a cosmetic defect for a silent one —
  // every 400 from review/agree (wrong status, closed period, separation of
  // duties) would vanish with no banner.
  //
  // `handle` itself is GONE: these were its only two call sites.
  const pickUp = useMutation({
    mutationFn: () => commissionsApi.review(id),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });
  const agree = useMutation({
    mutationFn: () => commissionsApi.agree(id),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });
  // Routed through the mutation rather than `handle` so `isPending` is real —
  // the button below advertises a loading state, and reading it off a mutation
  // nothing invokes would make that permanently false.
  const withdraw = useMutation({
    mutationFn: (reason: string | undefined) => commissionsApi.withdraw(id, reason),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  // 🔴 ACCEPTING A SUGGESTION IS NOT THE `acceptSuggestion` HANDLER BELOW.
  // They share a word and nothing else. That one calls `revise` with the values
  // Accounting attached to a CONTESTED claim; this calls `accept` →
  // `AcceptSuggestedAsync`, which admits `Suggested` only and moves the claim to
  // Submitted. Their status gates are mutually exclusive, so reusing the wrong
  // one would file a revision on a claim that has never been submitted — and
  // silently, because that handler early-returns when `suggestion` is null,
  // which it always is on a Suggested claim.
  //
  // Modelled on `withdraw`, deliberately: a `useMutation` with a real
  // `isPending` and an invalidation. The neighbouring `acceptSuggestion` is a
  // bare async function, so copying ITS shape would bypass `staleAfterSaveGate`.
  const accept = useMutation({
    mutationFn: () => commissionsApi.accept(id),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  // Pull the most recent Contested audit entry — if Accounting provided a
  // suggestedValues blob, surface a one-click "Accept Suggestion" button
  // that generates a revision with those values. Hooks above the early
  // returns so the call order stays stable.
  const latestContest = useMemo(
    () =>
      (detail.data?.auditEntries ?? [])
        .filter((a) => a.action === "Contested")
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0],
    [detail.data],
  );
  const suggestion = useMemo(() => {
    if (!latestContest?.suggestedValues) return null;
    try {
      return JSON.parse(latestContest.suggestedValues) as Record<string, number | string>;
    } catch {
      return null;
    }
  }, [latestContest]);

  if (detail.isLoading) {
    return (
      <div className="font-display text-label uppercase tracking-label text-gray-mid">
        Loading claim…
      </div>
    );
  }
  if (!detail.data) {
    return (
      <div>
        <div className="font-display font-bold text-h3 text-onyx">Claim not found.</div>
        <Link to="/commissions" className="text-gold-shadow hover:underline">
          Back to commissions
        </Link>
      </div>
    );
  }

  const c = detail.data.summary;
  const d = detail.data;
  const isMine = user?.id === c.claimantUserId;
  // Commission lockdown (2026-06): the reviewer / accounting actions
  // (begin-review, agree/contest, confirm-revenue, record-payment, adjustment)
  // are ADMIN-only. The backend gates them on Claims.Review / Claims.Agree /
  // Claims.Contest / Claims.ConfirmRevenue / Commission.Pay — all revoked from
  // non-admins (incl. B3) — so a non-admin must never see these affordances.
  const isReviewer = user?.roleCodes.includes("ADMIN");
  const canReview = isReviewer && !isMine;

  // 🔴 THE MONEY-OUT ACTIONS NEED A SECOND CLAUSE, because the server they call
  // has one. `CommissionPaymentService.SeparationOfDutiesViolation` refuses TWO
  // actors on the shared insert path every payment and adjustment goes through:
  // the claimant ("You are this commission's payee, so someone else must record
  // its payment") AND the person who confirmed the revenue ("You confirmed this
  // commission's revenue, so someone else must mark it paid"). Three people have
  // to touch a payout. `canReview` mirrors only the first clause, so it would
  // leave a button that 400s for one specific actor — which is the exact class
  // being fixed here, one clause further in.
  //
  // `revenueConfirmedByUserId` is already on the detail DTO; nothing new crosses
  // the wire for this.
  const canPayOut = canReview && d.revenueConfirmedByUserId !== user?.id;

  const acceptSuggestion = async () => {
    if (!suggestion) return;
    const gross =
      suggestion.GrossRevenue != null ? Number(suggestion.GrossRevenue) : c.grossRevenue;
    const pct =
      suggestion.CommissionPercent != null
        ? Number(suggestion.CommissionPercent)
        : c.commissionPercent;
    try {
      await commissionsApi.revise(c.id, {
        loanId: c.loanId ?? null,
        loanReference: c.loanReference ?? null,
        revenuePoolId: c.revenuePoolId,
        serviceDescription: c.serviceDescription,
        grossRevenue: gross,
        commissionPercent: pct,
        revisionSummary: "Accepted Accounting's suggested values.",
      });
      invalidate();
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e));
    }
  };

  return (
    <div className="flex flex-col gap-space-5 max-w-[1100px]">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-space-3">
        <div>
          <div className="flex items-center gap-space-3 mb-space-2">
            <span className="font-mono text-small text-gold-shadow">{c.claimNumber}</span>
            <ClaimStatusBadge status={c.status} />
            <RevisionStateBadge state={c.revisionState} />
            {d.legacyRevenueConfirmation && (
              <span
                className="inline-flex items-center px-space-2 py-[4px] rounded-sm bg-white-warm border border-border-light text-gray-mid font-display text-[10px] font-semibold uppercase tracking-[0.16em]"
                title="Revenue was confirmed under the pre-SP-5 rule (a typed date, no income-event linkage). It is honoured, not re-opened."
              >
                Confirmed under legacy rule
              </span>
            )}
            {c.isEscalated && (
              <span className="inline-flex items-center px-space-2 py-[4px] rounded-sm bg-status-warning text-white-pure font-display text-[10px] font-bold uppercase tracking-[0.16em]">
                Escalated — round {c.reviewRound}
              </span>
            )}
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            {c.poolCode} · {c.poolName}
          </h1>
          <p className="text-small text-gray-mid mt-space-2">
            Claimant:{" "}
            <ClaimClaimant
              status={c.status}
              claimantUserId={c.claimantUserId}
              claimantDisplayName={c.claimantDisplayName}
            />{" "}
            · Created {shortDate(c.createdAt, tz)}
          </p>
        </div>
        <div className="flex gap-space-2 flex-wrap">
          {isMine && c.status === "Draft" && (
            <Link to={`/commissions/${c.id}/edit`}>
              <Button variant="secondary">Edit</Button>
            </Link>
          )}
          {isMine && c.status === "Contested" && (
            <>
              {/* The THIRD exit from Contested. Before it existed the only two
                  were revise (which requires a field to CHANGE) and withdraw
                  (which forfeits) — so a claimant who believed the contested
                  figure was right had to alter something they believe is
                  correct. Secondary, not primary: revising is still the more
                  common answer to a contest. */}
              {/* Gated on revisionState for the same reason Resolve escalation is
                  gated on isEscalated: AffirmAsync refuses a claim with an edit in
                  progress, because affirming on top of changed values would stamp
                  "the original stands" over values that no longer are. Offering it
                  there produces a 400 the user cannot act on. Revise, beside it,
                  is exactly what such a claim needs. */}
              {c.revisionState !== "RevisionInProgress" && (
                <Button variant="secondary" onClick={() => setShowAffirm(true)}>
                  Affirm
                </Button>
              )}
              <Link to={`/commissions/${c.id}/edit`}>
                <Button variant="primary">Revise</Button>
              </Link>
            </>
          )}
          {/* 🔴 THE CONSTRUCTIVE HALF OF A DECISION THIS PAGE USED TO OFFER ONLY
              THE IRREVERSIBLE HALF OF. `Suggested` is in WITHDRAWABLE_STATUSES,
              so a claimant who opened their own suggestion to read it before
              deciding could decline it and nothing else — the accept lived only
              in the My Claims tray. Worse for an ADMIN who is also a claimant:
              `CommissionsPage` routes ADMIN to the Accounting review queue,
              which has no tray, so they had no accept path anywhere.

              The gate mirrors the server exactly — `AcceptSuggestedAsync`
              requires `Status == Suggested` and `ClaimantUserId == caller` — so
              the button cannot render where the call would 400. It fails closed
              on a HELD claim for free: `isMine` compares against an empty
              `claimantUserId`, which no signed-in user matches. Do not relax it
              to a bare status check.

              Rendered BEFORE Withdraw: the destructive action should not be the
              first thing under the cursor. */}
          {isMine && c.status === "Suggested" && (
            <Button
              variant="primary"
              loading={accept.isPending}
              onClick={() => accept.mutate()}
            >
              Accept
            </Button>
          )}
          {isMine && WITHDRAWABLE_STATUSES.includes(c.status) && (
            <Button
              variant="secondary"
              loading={withdraw.isPending}
              onClick={() => {
                setWithdrawReason("");
                setShowWithdraw(true);
              }}
            >
              Withdraw
            </Button>
          )}
          {/* The arbiter's door. `canReview` is already ADMIN && !claimant, which
              is exactly what ResolveEscalationAsync requires — the permission is
              ADMIN-only and the server refuses a claimant resolving their own
              escalation. Gated on isEscalated too: offering it otherwise produces
              a 400 the user cannot act on. Placed FIRST because an escalated
              claim's escalation is the thing to settle before anything else.

              The STATUS half matters as much as isEscalated: the flag survives
              withdrawal and settlement, so an escalated-then-withdrawn claim would
              otherwise render this button and 400 on submit. Same rule the server
              enforces and the queue filters on. */}
          {canReview && c.isEscalated && ESCALATION_ACTIONABLE_STATUSES.includes(c.status) && (
            <Button variant="primary" onClick={() => setShowResolveEscalation(true)}>
              Resolve escalation
            </Button>
          )}
          {canReview && c.status === "Submitted" && (
            <Button variant="primary" loading={pickUp.isPending} onClick={() => pickUp.mutate()}>
              Begin review
            </Button>
          )}
          {canReview && c.status === "UnderReview" && (
            <>
              <Button variant="secondary" onClick={() => setShowContest(true)}>
                Contest
              </Button>
              <Button
                variant="primary"
                loading={agree.isPending}
                onClick={() => agree.mutate()}
              >
                Agree
              </Button>
            </>
          )}
          {/* `canReview`, not `isReviewer`: `ConfirmRevenueAsync` is one of the
              module's four separation-of-duties guards and refuses the claimant
              outright ("You are this commission's payee, so someone else must
              confirm its revenue"). This was the only one of the six reviewer
              gates on this page still reading `isReviewer` bare — the three
              above it were already correct — so an ADMIN who is the claimant on
              their own Agreed claim was shown a button that 400s. The confirmer
              clause does not apply here: the claim is still Agreed, so
              `revenueConfirmedByUserId` is null by construction. */}
          {canReview && c.status === "Agreed" && (
            <Button variant="primary" onClick={() => setShowConfirmRevenue(true)}>
              Confirm revenue
            </Button>
          )}
          {/* Both of these post through the shared payment insert path, so both
              carry the two-clause guard — see `canPayOut` above. */}
          {canPayOut && (c.status === "Payable" || c.status === "PartiallyPaid") && (
            <>
              <Button variant="secondary" onClick={() => setShowAdjustment(true)}>
                Adjustment
              </Button>
              <Button variant="primary" onClick={() => setShowRecordPayment(true)}>
                Record payment
              </Button>
            </>
          )}
          {canPayOut && c.status === "PaidInFull" && (
            <Button variant="secondary" onClick={() => setShowAdjustment(true)}>
              Post adjustment
            </Button>
          )}
        </div>
      </div>

      {isMine && c.status === "Contested" && suggestion && (
        <div className="flex items-center justify-between gap-space-3 p-space-4 border border-gold rounded-md bg-gold/5">
          <div className="flex items-start gap-space-2">
            <Sparkles size={18} strokeWidth={1.5} className="text-gold shrink-0 mt-[2px]" aria-hidden />
            <div>
              <div className="font-display font-bold text-body text-onyx">
                Accounting suggested values
              </div>
              <div className="text-small text-gray-mid mt-space-1">
                {suggestion.GrossRevenue != null && (
                  <>Gross → {currency(Number(suggestion.GrossRevenue))}{" "}</>
                )}
                {suggestion.CommissionPercent != null && (
                  <>Percent → {percent(Number(suggestion.CommissionPercent))}</>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-space-2">
            <Button
              variant="secondary"
              onClick={() => navigate(`/commissions/${c.id}/edit`)}
            >
              Open and edit
            </Button>
            <Button variant="primary" onClick={acceptSuggestion}>
              Accept suggestion
            </Button>
          </div>
        </div>
      )}

      {/* Each dialog is mounted only while it is open, as Correct attribution
          is below, so every opening starts from the claim as it is now. Kept
          mounted, a dialog kept the last opening's entries: Record payment
          came back with the amount and reference of the payment just
          recorded, and Contest with the previous round's notes. */}
      {showContest && <ContestModal open onClose={() => setShowContest(false)} claim={d} />}
      {showAffirm && <AffirmModal open onClose={() => setShowAffirm(false)} claim={d} />}
      {showResolveEscalation && (
        <ResolveEscalationModal open onClose={() => setShowResolveEscalation(false)} claim={d} />
      )}
      {showConfirmRevenue && (
        <ConfirmRevenueModal open onClose={() => setShowConfirmRevenue(false)} claim={d} />
      )}
      {showRecordPayment && (
        <RecordPaymentModal open onClose={() => setShowRecordPayment(false)} claim={d} />
      )}
      {showAdjustment && <AdjustmentModal open onClose={() => setShowAdjustment(false)} claim={d} />}

      {/* Withdrawal is terminal and unrecoverable, and while a claim is
          contested it is the ONLY action a claimant has that always works — so
          it was the one control most likely to be reached for by mistake, and
          the only one with nothing in front of it. The reason is optional
          because the server treats it as optional (WithdrawAsync falls back to
          "Withdrawn by claimant"); this dialog is the first place it has ever
          been collectable, and it lands on the claim's audit trail. */}
      <ConfirmModal
        open={showWithdraw}
        title="Withdraw this claim?"
        destructive
        confirmLabel="Withdraw claim"
        cancelLabel="Keep claim"
        onCancel={() => setShowWithdraw(false)}
        onConfirm={() => {
          setShowWithdraw(false);
          withdraw.mutate(withdrawReason.trim() || undefined);
        }}
        message={
          <div className="flex flex-col gap-space-3">
            <p className="m-0">
              Withdrawing <span className="font-mono">{c.claimNumber}</span> for{" "}
              <span className="font-semibold tabular-nums">{currency(c.commissionAmount)}</span> is
              final. The claim moves to Withdrawn, a terminal state — it cannot be reopened,
              resubmitted or revised, and no commission will be paid on it.
            </p>
            {c.status === "Contested" && (
              <p className="m-0">
                This claim is contested. If you meant to answer Accounting's objection rather than
                give up the claim, close this and choose <span className="font-semibold">Revise</span>{" "}
                to change something, or <span className="font-semibold">Affirm</span> to send it back
                unchanged with your reasons.
              </p>
            )}
            <label className="flex flex-col gap-space-1">
              <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
                Reason (optional)
              </span>
              <textarea
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                rows={2}
                className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
                placeholder="Recorded on the claim's audit trail."
              />
            </label>
          </div>
        }
      />

      {error && (
        <div
          role="alert"
          className="p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small"
        >
          {error}
        </div>
      )}

      {isMine && isWaitingInReviewQueue(c.status) && position.data != null && (
        <div className="p-space-3 border border-border-light rounded-md bg-white-warm text-small text-gray-mid">
          You are <span className="font-semibold text-onyx">#{position.data}</span> in the review queue.
        </div>
      )}

      {/* Visual status timeline */}
      <Card>
        <div className="flex items-center justify-between gap-space-2 overflow-x-auto">
          {TIMELINE_ORDER.map((step, idx) => {
            const currentIdx = TIMELINE_ORDER.indexOf(c.status);
            const reached = currentIdx >= 0 && idx <= currentIdx;
            const isCurrent = step === c.status;
            return (
              <div key={step} className="flex items-center flex-1 min-w-[120px]">
                <div className="flex flex-col items-center text-center flex-1">
                  <div
                    className={
                      "h-8 w-8 rounded-full flex items-center justify-center border " +
                      (reached
                        ? "bg-gold border-gold text-onyx"
                        : "bg-white-pure border-border-light text-gray-mid")
                    }
                  >
                    {reached ? <Check size={16} strokeWidth={2} /> : <Circle size={12} strokeWidth={1.5} />}
                  </div>
                  <div
                    className={
                      "mt-space-1 font-display text-[10px] font-semibold uppercase tracking-label " +
                      (isCurrent ? "text-gold" : reached ? "text-onyx" : "text-gray-mid")
                    }
                  >
                    {claimStatusLabel(step)}
                  </div>
                </div>
                {idx < TIMELINE_ORDER.length - 1 && (
                  <div className={"h-px flex-1 " + (reached ? "bg-gold" : "bg-border-light")} />
                )}
              </div>
            );
          })}
        </div>
        {/* 2e — `Suggested` is the ENTRY state for every auto-suggested claim
            (both manual-create endpoints are ADMIN-only, so for every other user
            it is the ONLY state a claim can be born in), yet it is absent from
            TIMELINE_ORDER, whose seven steps are the MANUAL ladder.

            It is not appended to that array on purpose. A manually created claim
            starts at Draft and never passes through Suggested, so a Suggested
            step would be drawn for claims that never occupied it and marked
            "reached" for every Draft claim — asserting a history that did not
            happen. Suppressing the ladder entirely was the other option, and it
            discards the useful half: the path ahead is real and unchanged.

            So it takes the treatment this page already gives its two other
            off-ladder states. The `currentIdx >= 0` guard above means nothing is
            falsely completed; what was missing was any word for why the whole
            row is grey. */}
        {c.status === "Suggested" && (
          <div className="mt-space-3 p-space-3 border border-gold rounded-md bg-gold-highlight/10 text-small">
            <span className="font-display font-semibold uppercase text-label tracking-label text-gold-shadow">
              Before the timeline — Suggested
            </span>
            {/* 🔴 THE THIRD PERSON IS CORRECT FOR ONE AUDIENCE AND WRONG FOR
                THE OTHER, so the sentence branches rather than being rewritten.
                An ADMIN reviewer reaches this same page via Claims.ReadAll and
                is genuinely reading about someone else; the claimant IS "the
                claimant it is routed to", and telling them a third party will
                act is both false and a dead end — the Accept button above is
                theirs. Same `isMine` the button is gated on, so the copy and
                the affordance cannot disagree. */}
            <div className="text-onyx mt-space-1">
              {isMine
                ? "The system drafted this claim from the pool's allocation table. Accept it to submit it for review."
                : "The system drafted this claim from the pool's allocation table. It joins the path above at Submitted when the claimant it is routed to accepts it."}
            </div>
          </div>
        )}
        {/* 2c — rendered only when the server actually diagnosed the hold, so a
            build without the roster hook degrades to the "Unassigned — held"
            badge alone rather than to a confident sentence about nothing. */}
        {d.heldPosition && (
          <HeldPositionNote
            held={d.heldPosition}
            isRollup={c.loanId == null}
            heldReason={c.heldReason}
            heldReasonDetail={c.heldReasonDetail}
            /* L7/U7 — the door the note's own "an ADMIN correction to this
               loan's commission attribution is what releases it" sentence has
               been pointing at since WS4. Three conditions, and each rules out
               a control that could not do what it appears to: only a BOOK
               share is released by a correction, only a per-payment claim
               names the loan to correct (a roll-up spans many, and its detail
               lists them with their own links), and the endpoint is
               ADMIN-only. HIDDEN rather than disabled, per the house rule. */
            action={
              d.heldPosition.routing === "Book" &&
              c.loanId &&
              hasPermission("Commission.ManageAllocations") ? (
                <Button
                  variant="secondary"
                  onClick={() => setShowCorrectAttribution(true)}
                >
                  Correct attribution
                </Button>
              ) : null
            }
          />
        )}
        {showCorrectAttribution && c.loanId && (
          <CorrectAttributionModal
            loanId={c.loanId}
            open
            onClose={() => setShowCorrectAttribution(false)}
          />
        )}
        {c.status === "Contested" && (
          <div className="mt-space-3 p-space-3 border border-status-warning rounded-md bg-status-warning/5 text-small">
            <span className="font-display font-semibold uppercase text-label tracking-label text-status-warning">
              Off timeline — Contested
            </span>
            <div className="text-onyx mt-space-1">
              The claim is back with the claimant for revision. It will rejoin the path after resubmission.
            </div>
          </div>
        )}
        {c.status === "Withdrawn" && (
          <div className="mt-space-3 p-space-3 border border-border-light rounded-md bg-white-warm text-small text-gray-mid">
            <div>Claim withdrawn — terminal state.</div>
            {/* 2d — `DisqualificationReason` has three writers and had zero
                readers: the claimant's own stated reason for withdrawing, the
                reversal sweep's, and the recurring roll-up's supersession —
                which PR #1161 deliberately SPLIT into two distinct messages
                ("the position was filled" vs "the held scope for this position
                changed") because saying the first over the second would be
                false. That distinction was written into the column and shown to
                nobody. Rendered plainly rather than styled as an alert: on the
                dominant path it is a note the reader wrote themselves. */}
            {d.disqualificationReason && (
              <div className="mt-space-1 text-onyx whitespace-pre-wrap">
                {d.disqualificationReason}
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-space-4">
        <div className="flex flex-col gap-space-4">
          <Card>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-3">
              Claim data
            </div>
            <div className="grid grid-cols-2 gap-x-space-4 gap-y-space-3">
              <Field label="Pool" value={`${c.poolCode} — ${c.poolName}`} />
              <Field
                label="Loan"
                value={
                  <ClaimLoan
                    loanId={c.loanId}
                    loanNumber={c.loanNumber}
                    loanReference={c.loanReference}
                  />
                }
              />
              <Field label="Gross revenue" value={currency(c.grossRevenue)} mono />
              <Field label="Commission percent" value={percent(c.commissionPercent)} mono />
              <Field label="Commission amount" value={currency(c.commissionAmount)} mono bold />
              <Field label="Total paid" value={currency(c.totalPaid)} mono />
              <Field label="Balance remaining" value={currency(c.balanceRemaining)} mono bold />
              <Field label="Review round" value={c.reviewRound > 0 ? String(c.reviewRound) : "—"} />
              <Field label="Period" value={`${c.periodYear}-${String(c.periodMonth).padStart(2, "0")}`} />
              <Field label="Revenue received" value={shortDate(d.revenueReceivedDate, tz)} />
              {(d.revenueConfirmedByUserId || d.revenueConfirmedByDisplayName) && (
                <Field
                  label="Confirmed by"
                  value={d.revenueConfirmedByDisplayName ?? d.revenueConfirmedByUserId ?? "—"}
                />
              )}
            </div>
            <div className="mt-space-4">
              <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
                Service description
              </div>
              <div className="text-body text-onyx whitespace-pre-wrap">{c.serviceDescription}</div>
            </div>
            {d.fundingEvents && d.fundingEvents.length > 0 && (
              <div className="mt-space-4">
                <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
                  Funding income
                </div>
                <ul className="flex flex-col gap-space-1 m-0 p-0 list-none">
                  {d.fundingEvents.map((f) => {
                    // A recurring pool rolls a whole month of income into ONE
                    // claim, so two links can share an amount and a date and
                    // still be different kinds of income (a Pool K monthly
                    // accrual beside its one-off liquidity leg). The source is
                    // what tells them apart. Prepended rather than substituted,
                    // so a payload without it renders exactly as before.
                    const source = f.sourceLabel ?? f.sourceType;
                    return (
                      <li
                        key={f.id}
                        className="flex items-center justify-between gap-space-2 text-small border-b border-border-light/50 py-space-1"
                      >
                        <span className="text-gray-mid">
                          {source ? `${source} · ` : ""}Recognized {shortDate(f.recognizedAt, tz)}
                        </span>
                        <span className="tabular-nums font-semibold text-onyx">{currency(f.amount)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {/* `!c.isEscalated` is belt-and-braces beside the server-side clear.
                EscalationResolution now has a clearer at both writers of
                IsEscalated, so this can only differ for a row that already
                carried a stale mirror when that shipped — which the server fix
                cannot reach and this condition can. */}
            {d.escalationResolution && !c.isEscalated && (
              <div className="mt-space-4 p-space-3 border border-border-light rounded-md bg-white-warm">
                <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-space-1">
                  Escalation resolution
                </div>
                <div className="text-small text-onyx">{d.escalationResolution}</div>
              </div>
            )}
          </Card>

          {d.payments.length > 0 && (
            <Card>
              <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-3">
                Payment history
              </div>
              <table className="w-full text-small">
                <thead>
                  <tr className="border-b border-border-light">
                    <th className="text-left py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Date
                    </th>
                    <th className="text-left py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Payment #
                    </th>
                    <th className="text-left py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Method
                    </th>
                    <th className="text-left py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Reference
                    </th>
                    <th className="text-left py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Type
                    </th>
                    <th className="text-right py-space-2 font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.payments.map((p) => (
                    <tr key={p.id} className="border-b border-border-light/50">
                      <td className="py-space-2">{calendarDate(p.paymentDate)}</td>
                      <td className="py-space-2 font-mono">{p.paymentNumber}</td>
                      <td className="py-space-2">{p.paymentMethod}</td>
                      <td className="py-space-2">{p.referenceNumber}</td>
                      <td className="py-space-2">
                        {p.adjustmentType ? (
                          <span className="font-display text-[10px] font-semibold uppercase tracking-label text-status-warning">
                            {p.adjustmentType}
                          </span>
                        ) : (
                          "Payment"
                        )}
                      </td>
                      <td
                        className={
                          "py-space-2 text-right tabular-nums font-semibold " +
                          (p.amount < 0 ? "text-status-error" : "text-onyx")
                        }
                      >
                        {currency(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <DiscussionThread claimId={c.id} />
        </div>

        {/* Right sidebar: audit trail */}
        <Card dark>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold mb-space-3">
            Activity
          </div>
          <ul className="flex flex-col gap-space-3 m-0 p-0 list-none">
            {d.auditEntries.length === 0 && (
              <li className="text-small text-white-pure/60">No activity yet.</li>
            )}
            {d.auditEntries.map((a) => (
              <AuditItem key={a.id} entry={a} tz={tz} />
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  bold,
}: {
  label: string;
  // ReactNode, not string: the loan field renders a link to its loan.
  value: ReactNode;
  mono?: boolean;
  bold?: boolean;
}) {
  return (
    <div>
      <div className="font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid mb-space-1">
        {label}
      </div>
      <div
        className={
          "text-body text-onyx " +
          (mono ? "tabular-nums " : "") +
          (bold ? "font-bold" : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

function AuditItem({ entry, tz }: { entry: CommissionAuditLogDto; tz: string | null }) {
  // Admin User Impersonation. The claimant stays the actor of record — the
  // session really was minted as them, and every separation-of-duties check in
  // the service compares against that identity — so the admin is ADDED beside
  // them, never substituted. An ordinary row renders exactly as before.
  //
  // BLANK, not just null. `??` alone would pass an empty display name through
  // as "", which is falsy, which suppresses the whole clause below — rendering
  // an impersonated row byte-identical to a genuine one while the id sits
  // populated and unused in the DTO. That is precisely the indistinguishability
  // this change exists to end, and it is reachable: ProfileService's
  // self-service update is the one field of seven not routed through
  // NullIfEmpty, so an admin can set their own DisplayName to "". The raw id is
  // the safe last resort — HttpImpersonationContext returns null rather than a
  // blank claim, so it can never itself be empty.
  //
  // The actor cell below keeps its plain `??` deliberately: a blank name there
  // costs a name, not a security marker, and widening it would change how every
  // ordinary row renders.
  const impersonatorName = entry.impersonatedByDisplayName?.trim();
  const impersonator = impersonatorName ? impersonatorName : entry.impersonatedByUserId;
  const contestedFields = formatContestedFields(entry.contestedFields);
  return (
    <li className="border-b border-border-dark pb-space-3 last:border-0">
      <div className="flex items-start gap-space-2">
        <CheckCircle2 size={16} strokeWidth={1.5} className="text-gold shrink-0 mt-[2px]" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold">
            {entry.action}
          </div>
          <div className="text-small text-white-pure/80 mt-space-1">
            {entry.userDisplayName ?? entry.userId}
            {impersonator && (
              <span className="text-status-warning"> (via {impersonator})</span>
            )}{" "}
            · {datetime(entry.timestamp, tz)}
          </div>
          {entry.notes && (
            <div className="text-small text-white-pure mt-space-1 whitespace-pre-wrap">{entry.notes}</div>
          )}
          {contestedFields && (
            <div className="text-caption text-white-pure/60 mt-space-1">
              Fields: {contestedFields}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
